import React, { useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';

import BlueArrow from './images/arrow-blue.svg';
// import GoldMicrophone from './images/microphone-gold.svg';
// import GrayDiamond from './images/diamond-gray.svg';
import LeftArrow from './images/left-arrow.svg';
import RightArrow from './images/right-arrow.svg';

import css from './SectionUpcoming.module.css';
import { AvatarSquareLarge } from '../../../../components/AvatarSquare/AvatarSquare';
import { NamedLink } from '../../../../components';
import { DATE_TYPE_DATE, DATE_TYPE_DATETIME } from '../../../../util/types';
import { FormattedDate } from 'react-intl';
import TextLimitedLength from '../../../../components/TextLimitedLength/TextLimitedLength';

const BookingPeriod = props => {
  const { startDate, endDate, dateType, timeZone } = props;
  const timeZoneMaybe = timeZone ? { timeZone } : null;

  const timeFormatOptions =
    dateType === DATE_TYPE_DATE
      ? {
          weekday: 'long',
        }
      : {
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
        };

  const dateFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSectionLeft}>
          <div className={css.dayInfo}>
            <FormattedDate value={startDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={startDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
        </div>
        {'-'}
        <div className={css.bookingPeriodSectionRight}>
          <div className={css.dayInfo}>
            <FormattedDate value={endDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={endDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
        </div>
      </div>
    </>
  );
};

const BookingDate = props => {
  const { booking, listing } = props;

  if (!booking) {
    return null;
  }

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = booking.attributes;
  const localStartDate = displayStart || start;
  const localEndDateRaw = displayEnd || end;

  const endDay = localEndDateRaw;

  return (
    <div className={css.lineItem}>
      <BookingPeriod
        startDate={localStartDate}
        endDate={endDay}
        dateType={DATE_TYPE_DATETIME}
        timeZone={timeZone}
      />
    </div>
  );
};

const Swiper =
  typeof window !== 'undefined'
    ? require('swiper/react').Swiper
    : loadable(() => import('swiper/react'), {
        ssr: false,
      });
const SwiperSlide =
  typeof window !== 'undefined'
    ? require('swiper/react').SwiperSlide
    : loadable(() => import('swiper/react'), {
        ssr: false,
      });
const Navigation =
  typeof window !== 'undefined'
    ? require('swiper/modules').Navigation
    : loadable(() => import('swiper/modules'), {
        ssr: false,
      });

const SectionUpcoming = props => {
  const {
    config,
    upcomingSessions,
    _queryUpcomingSessionsError,
    _queryUpcomingSessionsInProgress,
    onQueryUpcomingSessions,
  } = props;
  const [currentSlideIndex, setCurrentSlideIndex] = useState(1);
  const swiperRef = useRef();

  useEffect(() => {
    // Load swiper CSS only in the browser
    if (typeof window !== 'undefined') {
      require('swiper/css/bundle');
      require('swiper/css/navigation');
    }

    if (!upcomingSessions) {
      onQueryUpcomingSessions('order', config);
    }
  }, []);

  // console.log({ upcomingSessions });
  const _isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className={css.root}>
      <div className={css.headingContainer}>
        <div className={css.heading}>Upcoming sessions</div>

        {upcomingSessions && (
          <div className={css.slidesCounter}>
            <img
              src={LeftArrow}
              className="hover"
              onClick={() => {
                if (swiperRef) {
                  swiperRef.current.swiper.slidePrev();
                }
              }}
            />
            {`${currentSlideIndex} / ${upcomingSessions?.length}`}
            <img
              src={RightArrow}
              className="hover"
              onClick={() => {
                if (swiperRef) {
                  swiperRef.current.swiper.slideNext();
                }
              }}
            />
          </div>
        )}
      </div>
      <div className={css.sliderContainer}>
        {upcomingSessions ? (
          <Swiper
            className={css.swiper}
            ref={swiperRef}
            modules={[Navigation]}
            navigation
            loop={true}
            slidesPerView={1}
            onSlideChange={swiper => setCurrentSlideIndex(swiper.realIndex + 1)}
          >
            {upcomingSessions?.map((session, index) => {
              const fullName = session.listing.attributes.title;
              const headline = session.listing.attributes.publicData.headline;
              const bio = session.listing.attributes.publicData.bio;
              const expertise = session.listing.attributes.publicData.expertise;

              return (
                <SwiperSlide key={index}>
                  <div className={css.slideContainer}>
                    <div className={css.slideContainer}>
                      <div className={css.imageContainer}>
                        <AvatarSquareLarge
                          user={session.provider}
                          className={css.avatarDesktop}
                          disableProfileLink
                        />
                      </div>
                      <div className={css.slideContent}>
                        <div className={css.slideHeading}>
                          <div className="col grow">
                            <p className={css.fullName}>{fullName}</p>
                            <p className={css.headline}>{headline}</p>
                            <TextLimitedLength rootClassName={css.bio} text={bio} maxLength={100} />

                            <div className={css.slideFooter}>
                              <div className={css.tagsContainer}>
                                {expertise?.map((expertise, tagIndex) => (
                                  <span className={css.tag} key={tagIndex}>
                                    {expertise}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div>
                            <div className={css.date}>
                              <BookingDate booking={session.booking} listing={session.listing} />
                            </div>
                          </div>
                        </div>

                        <NamedLink
                          className={css.link}
                          name={'OrderDetailsPage'}
                          params={{ id: session.id.uuid }}
                        >
                          <span>See details</span>
                          <img src={BlueArrow} />
                        </NamedLink>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <p>No upcoming sessions yet. Pick one of your best matches or search for mentors!</p>
        )}
      </div>
    </div>
  );
};

export default SectionUpcoming;
